import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { type FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { UserRolesSchema, type UserRolesSchemaType } from "./userRoles.schema"

import { Button } from "@/components/common/Button"
import { SelectDialogContent } from "@/components/common/Modals"
import type { Role } from "@/graphql/codegen/graphql"
import type { DialogCloseAction } from "@/types/common"

export enum DialogType {
  ADD = "ADD",
  EDIT = "EDIT",
}

interface UserRolesDialogProps {
  open: boolean
  rolesList: Role[]
  userRoles: Role[]
  dialogType: DialogType
  onClose: (action: DialogCloseAction) => void
  onSubmit: (roles: string[]) => void
}

export const UserRolesDialog: FC<UserRolesDialogProps> = ({
  open,
  rolesList,
  userRoles,
  dialogType,
  onClose,
  onSubmit,
}) => {
  const methods = useForm({
    defaultValues: {
      roles: userRoles,
    },
    resolver: zodResolver(UserRolesSchema),
    mode: "all",
  })

  const onHandleSubmit = (data: UserRolesSchemaType) => {
    onSubmit(data.roles.map(({ roleId }) => roleId))
    methods.reset()
    onClose("success")
  }

  const isButtonEnabled = methods.formState.isValid && methods.formState.isDirty

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        methods.reset()
        onClose(reason)
      }}
      fullWidth
    >
      <FormProvider {...methods}>
        <DialogTitle className='pb-3 pl-10 font-normal text-primary'>
          {dialogType === DialogType.ADD ? "Add" : "Edit"} User Roles
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => {
            methods.reset()
            onClose("cancel")
          }}
          className='absolute right-4 top-4 text-primary'
        >
          <CloseOutlined />
        </IconButton>
        <Divider />
        <DialogContent classes={{ root: "p-10" }}>
          <SelectDialogContent<Role>
            options={rolesList}
            title='User Roles'
            label='Add roles to this user'
            idField='roleId'
            nameField='name'
            controlName='roles'
            placeholder='Select At Least One'
          />
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button
            appearance='outlined'
            onClick={() => {
              methods.reset()
              onClose("cancel")
            }}
          >
            Cancel
          </Button>
          <Button onClick={methods.handleSubmit(onHandleSubmit)} disabled={!isButtonEnabled}>
            Save
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
